/**
 * CustomerTierDistribution — Phase T2 follow-up (Feb 2026).
 *
 * Compact horizontal bar chart of the customer cohort by tier. Renders
 * inline anywhere super-admin / dispatcher dashboards want a fast
 * "what does our base look like?" answer.
 *
 * Currently surfaced on:
 *   - `/dispatch/admin/daily-brief-archive` (right pane sticky strip)
 *   - `/dispatch/admin/login-stats`
 *
 * Single fetch on mount; no realtime updates (cohort moves slowly).
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Crown, Sparkles, Award, Leaf } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { key: 'whale',   label: 'ONYX',     color: '#BF5AF2', icon: Crown },
  { key: 'vip',     label: 'PLATINUM', color: '#00F0FF', icon: Sparkles },
  { key: 'regular', label: 'GOLD',     color: '#FFD700', icon: Award },
  { key: 'new',     label: 'SILVER',   color: '#C0C0C0', icon: Leaf },
];


export function CustomerTierDistribution({ compact = false }) {
  const [data, setData] = useState(null);
  const [migration, setMigration] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dist, mig] = await Promise.all([
        axios.get(`${API_URL}/api/admin/customer-tier-distribution`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/customer-tier-migration?days=30`, { withCredentials: true }),
      ]);
      setData(dist.data);
      setMigration(mig.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="border border-[#2A0F3D] bg-[#0A0008] px-4 py-3 font-mono text-xs text-[#A3C7A8]">
        <span className="cursor-blink">█</span> Loading tier distribution...
      </div>
    );
  }
  if (!data) return null;

  const total = data.total || 0;
  return (
    <div
      className="border border-[#2A0F3D] bg-[#0A0008] p-4"
      data-testid="customer-tier-distribution"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#39FF14]">
          // CUSTOMER COHORT
        </h3>
        <span className="font-mono text-[10px] text-[#A3C7A8]">
          {total.toLocaleString()} total
        </span>
      </div>
      <div className="space-y-2">
        {TIERS.map(({ key, label, color, icon: Icon }) => {
          const count = data.counts?.[key] || 0;
          const pct = data.percent?.[key] || 0;
          const rev = data.revenue?.[key] || 0;
          return (
            <div key={key} className="flex items-center gap-3" data-testid={`tier-dist-row-${key}`}>
              <div
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider w-20 shrink-0"
                style={{ color }}
              >
                <Icon className="w-3 h-3" />
                {label}
              </div>
              <div className="flex-1 h-5 bg-[#0C0612] border border-[#2A0F3D] relative overflow-hidden">
                <div
                  className="h-full transition-all duration-700"
                  style={{ width: `${pct}%`, background: color, opacity: 0.8 }}
                />
                <span className="absolute inset-0 flex items-center px-2 font-mono text-[10px] text-white drop-shadow">
                  {count} <span className="ml-1 opacity-60">({pct}%)</span>
                </span>
              </div>
              {!compact && (
                <span className="font-mono text-[10px] text-[#6B8E73] w-20 text-right shrink-0">
                  ${rev.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {migration && (migration.promotions_total > 0 || migration.demotions_total > 0) && (
        <div className="mt-3 pt-3 border-t border-[#2A0F3D]" data-testid="tier-migration-strip">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#6B8E73] mb-1.5">
            // LAST {migration.window_days}D MIGRATION
          </div>
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap font-mono text-[11px]">
            {migration.promotions.whale > 0 && (
              <span style={{ color: '#BF5AF2' }} data-testid="tier-mig-promo-whale">↑ {migration.promotions.whale} to ONYX</span>
            )}
            {migration.promotions.vip > 0 && (
              <span style={{ color: '#00F0FF' }} data-testid="tier-mig-promo-vip">↑ {migration.promotions.vip} to PLATINUM</span>
            )}
            {migration.promotions.regular > 0 && (
              <span style={{ color: '#FFD700' }} data-testid="tier-mig-promo-regular">↑ {migration.promotions.regular} to GOLD</span>
            )}
            {migration.demotions_total > 0 && (
              <span className="text-[#FF3B30]" data-testid="tier-mig-demotion">
                ↓ {migration.demotions_total} demoted (locks expired)
              </span>
            )}
            {migration.fresh_demotion_count > 0 && (
              <span className="text-[#FFD60A] text-[10px] uppercase" title="Detected just now by the on-the-fly sweep">
                {migration.fresh_demotion_count} fresh
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerTierDistribution;
